import { useLocation, useNavigate, useParams } from 'react-router';
import { OidcProtectedComponent, useOidc } from '@nohara/react-oidc';
import { PropsWithChildren, useEffect } from 'react';
import { Box, Fade } from '@mui/material';
import { permission } from '@nohara/oidc-core';

import NotFoundRoute from '@/app/routes/not-found';
import { OrgLayout } from '@/components/layouts';
import { paths } from '@/config/paths';
import { LoadingScreen } from '@/components/ui';
import { useOrg, useUserOrgs } from '@/features/orgs/api';

const permissions = [permission.org.read];

export const OrgSelectCheck = ({ children }: PropsWithChildren) => {
  const { orgId } = useParams<{ orgId: string }>();
  const { userId } = useOidc();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: orgs, isLoading: isLoadingOrgs } = useUserOrgs(userId);

  const redirectToOrg =
    !isLoadingOrgs &&
    (pathname === paths.org.select.getHref() || pathname === paths.org.root.path) &&
    orgs?.length === 1 &&
    !orgId
      ? orgs[0].id
      : undefined;

  const selectedOrgId = orgId ?? redirectToOrg;

  const initialOrgData = orgs?.find((o) => o.id === selectedOrgId);

  const { data: org, isLoading: isLoadingOrg } = useOrg(selectedOrgId, {
    queryConfig: {
      // cache the org data so it can go straight to the org layout
      initialData: initialOrgData,
    },
  });

  useEffect(() => {
    if (redirectToOrg) {
      navigate(paths.org.root.getHref(redirectToOrg), { viewTransition: false, replace: true });
    }
  }, [navigate, redirectToOrg]);

  if (isLoadingOrg || isLoadingOrgs) {
    return <LoadingScreen />;
  }

  if (orgId && !org) {
    return <NotFoundRoute />;
  }

  return (
    <OidcProtectedComponent
      requireAll={selectedOrgId ? permissions : []}
      context={{ orgId: selectedOrgId }}
      fallbackElement={<NotFoundRoute />}
    >
      <OrgLayout orgId={selectedOrgId}>
        {!redirectToOrg && (
          <Fade in timeout={200}>
            <Box>{children}</Box>
          </Fade>
        )}
      </OrgLayout>
    </OidcProtectedComponent>
  );
};
